import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Check, Key, Copy, Ban, Trash2, AlertTriangle } from 'lucide-react';

export function GrantDialog({
  open,
  onOpenChange,
  selectedUser,
  grantForm,
  setGrantForm,
  onSubmit,
  submitting,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle>Grant Free Subscription</DialogTitle>
        </DialogHeader>
        {selectedUser && (
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-700 rounded-sm">
              <p className="font-medium">{selectedUser.name}</p>
              <p className="text-sm text-slate-400">{selectedUser.email}</p>
            </div>
            
            <div>
              <Label className="text-slate-300">Plan</Label>
              <Select
                value={grantForm.plan_id}
                onValueChange={(val) => setGrantForm({ ...grantForm, plan_id: val })}
              >
                <SelectTrigger className="rounded-sm mt-1 bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="base">Base ($19/mo value)</SelectItem>
                  <SelectItem value="pro">Pro ($29/mo value)</SelectItem>
                  <SelectItem value="business">Business ($79/mo value) - Unlimited</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-300">Duration</Label>
              <Select
                value={grantForm.duration_months.toString()}
                onValueChange={(val) => setGrantForm({ ...grantForm, duration_months: parseInt(val) })}
              >
                <SelectTrigger className="rounded-sm mt-1 bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="1">1 Month</SelectItem>
                  <SelectItem value="3">3 Months</SelectItem>
                  <SelectItem value="6">6 Months</SelectItem>
                  <SelectItem value="12">12 Months (1 Year)</SelectItem>
                  <SelectItem value="24">24 Months (2 Years)</SelectItem>
                  <SelectItem value="36">36 Months (3 Years)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={onSubmit}
              disabled={submitting}
              className="w-full rounded-sm bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                'Granting...'
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Grant {grantForm.duration_months} Month{grantForm.duration_months > 1 ? 's' : ''} of {grantForm.plan_id}
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PasswordResetDialog({
  open,
  onOpenChange,
  selectedUser,
  newPassword,
  setNewPassword,
  onGeneratePassword,
  onSubmit,
  submitting,
  onCopyToClipboard,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-yellow-400" />
            Reset User Password
          </DialogTitle>
        </DialogHeader>
        {selectedUser && (
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-700 rounded-sm">
              <p className="font-medium">{selectedUser.name}</p>
              <p className="text-sm text-slate-400">{selectedUser.email}</p>
            </div>
            
            <div>
              <Label className="text-slate-300">New Password</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="rounded-sm bg-slate-700 border-slate-600 flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={onGeneratePassword}
                  className="rounded-sm border-slate-600 hover:bg-slate-700"
                >
                  Generate
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Minimum 6 characters</p>
            </div>

            {newPassword && (
              <div className="p-3 bg-yellow-900/30 border border-yellow-500/30 rounded-sm">
                <p className="text-xs text-yellow-400 mb-2">Share this password with the user:</p>
                <div className="flex items-center justify-between bg-slate-900 p-2 rounded">
                  <code className="text-green-400 font-mono">{newPassword}</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onCopyToClipboard(newPassword)}
                    className="h-8 text-slate-400 hover:text-white"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            
            <Button
              onClick={onSubmit}
              disabled={submitting || !newPassword || newPassword.length < 6}
              className="w-full rounded-sm bg-yellow-600 hover:bg-yellow-700"
            >
              {submitting ? (
                'Resetting...'
              ) : (
                <>
                  <Key className="h-4 w-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function SuspendDialog({
  open,
  onOpenChange,
  selectedUser,
  reason,
  setReason,
  onSubmit,
  submitting,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-400" />
            Suspend Account
          </DialogTitle>
        </DialogHeader>
        {selectedUser && (
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-700 rounded-sm">
              <p className="font-medium">{selectedUser.name}</p>
              <p className="text-sm text-slate-400">{selectedUser.email}</p>
            </div>
            
            <div className="p-4 bg-orange-900/30 border border-orange-500/30 rounded-sm">
              <p className="text-sm text-orange-300">
                <strong>Warning:</strong> Suspending this account will prevent the user from logging in. 
                Their data will be preserved and you can reactivate the account later.
              </p>
            </div>

            <div>
              <Label className="text-slate-300">Reason for Suspension (Optional)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Non-payment, Terms violation, User request..."
                className="rounded-sm mt-1 bg-slate-700 border-slate-600"
                rows={3}
              />
            </div>
            
            <Button
              onClick={onSubmit}
              disabled={submitting}
              className="w-full rounded-sm bg-orange-600 hover:bg-orange-700"
            >
              {submitting ? (
                'Suspending...'
              ) : (
                <>
                  <Ban className="h-4 w-4 mr-2" />
                  Suspend Account
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DeleteUserDialog({
  open,
  onOpenChange,
  selectedUser,
  onSubmit,
  submitting,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <Trash2 className="h-5 w-5" />
            Delete User
          </DialogTitle>
        </DialogHeader>
        {selectedUser && (
          <div className="space-y-4">
            <div>
              <p className="font-medium">{selectedUser.name}</p>
              <p className="text-sm text-slate-400">{selectedUser.email}</p>
            </div>
            
            <div className="p-4 bg-red-900/30 border border-red-500/30 rounded-sm">
              <p className="text-sm text-red-300">
                <strong>DANGER:</strong> This action cannot be undone! Deleting this user will permanently remove:
              </p>
              <ul className="text-sm text-red-300 mt-2 list-disc list-inside space-y-1">
                <li>The user account</li>
                <li>All leads, projects, and clients</li>
                <li>All photos, documents, and activities</li>
                <li>All team members (if owner)</li>
                <li>The entire organization data</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1 rounded-sm border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={onSubmit}
                disabled={submitting}
                className="flex-1 rounded-sm bg-red-600 hover:bg-red-700"
              >
                {submitting ? (
                  'Deleting...'
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Permanently
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
